// A stake table filling (PF-07): what its board shows and what happens when the time runs out.
// Plain TS (no React, no SDK) so the rules are tested on their own; the lobby feeds it the server's
// deadline (`stakeFillBy`, server ms) and Lagos Life's last word about the table.
import { stakePresetFor } from '@wahala/config';

/** The game a stake table plays: 2 seats race head to head, 4 play The Park. */
export type StakeGame = 'park' | 'race';

export function stakeGame(seats: number, preset?: string): StakeGame {
  if (preset === 'stakesRace') return 'race';
  if (preset === 'stakes4p') return 'park';
  return stakePresetFor(seats) === 'stakesRace' ? 'race' : 'park';
}

/** Where the table stands, for its board. */
export type FillState =
  /** Seats still empty and time left. */
  | 'waiting'
  /** Every seat taken: the round is about to start. */
  | 'full'
  /** Time ran out with seats empty: the fare goes back (live). */
  | 'expired'
  /** Staging only: solo players took the empty seats; a practice round, no fare on it. */
  | 'solo';

export interface FillInput {
  /** The server's deadline for the table to fill (absolute server ms); absent: no countdown. */
  fillBy?: number | undefined;
  /** The server's clock now (park.net.serverNow()). */
  now: number;
  seats: number;
  seated: number;
  /** Lagos Life's status for the match. */
  status: 'open' | 'playing' | 'done' | 'cancelled' | 'void';
  /** The park's frame says solo players took the empty seats (staging). */
  solo?: boolean | undefined;
}

export function fillState(i: FillInput): FillState {
  if (i.solo) return 'solo';
  if (i.status === 'playing' || i.seated >= i.seats) return 'full';
  if (i.fillBy !== undefined && i.now >= i.fillBy) return 'expired';
  return 'waiting';
}

/** Milliseconds left to fill, never below 0 (0 with no deadline). */
export const fillLeft = (fillBy: number | undefined, now: number): number =>
  fillBy === undefined ? 0 : Math.max(0, fillBy - now);

/** "2:41": the time left, rounded up so 0:00 shows only when it has run out. */
export function clock(ms: number): string {
  const s = Math.ceil(Math.max(0, ms) / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** What the phone does when a table it waits at runs out of time, or solo players take it. */
export interface ExpiryDeps {
  /** Leave the Lagos Life match: Lagos Life gives the fare back. */
  leaveTable: () => Promise<void>;
  /** Leave our stake park (expired only: a solo round plays on). */
  leavePark: () => void;
  /** Tell the player, and offer what comes next. */
  offer: (o: { game: StakeGame; stake: number; why: 'expired' }) => void;
}

/**
 * Run once per table: an expired table is left at Lagos Life and in our park, then the free round
 * is offered; a table solo players took is left at Lagos Life only (no fare may ride on that round).
 * Returns false when there is nothing to do.
 */
export async function onFillEnd(
  state: FillState,
  t: { game: StakeGame; stake: number },
  d: ExpiryDeps,
): Promise<boolean> {
  if (state === 'expired') {
    await d.leaveTable().catch(() => {});
    d.leavePark();
    d.offer({ game: t.game, stake: t.stake, why: 'expired' });
    return true;
  }
  if (state === 'solo') {
    await d.leaveTable().catch(() => {});
    return true;
  }
  return false;
}
